import React, { useState, useEffect, useRef } from 'react';
import { GlobalInput } from './GlobalInput';
import { GlobalButton } from './GlobalButton';
import { GlobalDropdown } from './GlobalDropdown';
import { Toast } from './Toast';
import EmployeeImportPreview from './EmployeeImportPreview';
import { collection, addDoc, doc, updateDoc, arrayUnion, query, where, getDocs } from 'firebase/firestore';
import { getLocalizedUserRoleOptions } from '../utils/userRoles';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useToast } from '../context/ToastProvider';
import { db } from '../lib/firebase';
import { useUser } from '../context/UserContext';
import { useCompanies } from '../hooks/useCompanies';
import { useProjects } from '../hooks/useProjects';
import styles from './InvitationModal.module.css';

/** Company ID for internal admins (THESCON internal) – used when role is internal_admin */
const INTERNAL_ADMIN_COMPANY_ID = 'OKGCziNDxHTksvd9Iei5';

interface InvitationData {
  email: string;
  firstName: string;
  secondName: string;
  role?: string;
  projectId?: string;
  sendEmail?: boolean;
  companyId?: string;
}


interface InvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  existingUserData?: {
    name?: string;
    email?: string;
    role?: string;
  };
  onUserAdded?: () => void; // Callback to refresh user list
  showRoleSelection?: boolean; // Only internal_admin can select roles
  preselectedCompanyId?: string; // Company ID to preselect (from company detail view)
}

export default function InvitationModal({
  isOpen,
  onClose,
  userId,
  existingUserData,
  onUserAdded,
  showRoleSelection = false,
  preselectedCompanyId
}: InvitationModalProps) {
  const { t } = useLocale();
  const showToast = useToast();
  const { role: currentUserRole, firstName, lastName, email: currentUserEmail, company: userCompany } = useUser();
  const { companies, loading: companiesLoading } = useCompanies();
  const { projects, loading: projectsLoading } = useProjects(isOpen);
  
  // Filter companies based on user role - client_admin can only see their own company
  const filteredCompanies = currentUserRole === 'client_admin' 
    ? companies.filter(company => company.id === userCompany)
    : companies;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeTab, setActiveTab] = useState(0);
  
  // Determine initial company selection
  const getInitialCompanyId = () => {
    if (preselectedCompanyId) return preselectedCompanyId;
    if (currentUserRole === 'internal_admin') return INTERNAL_ADMIN_COMPANY_ID;
    // For client_admin, auto-select their own company
    if (currentUserRole === 'client_admin' && userCompany) return userCompany;
    return '';
  };
  
  // Determine default role based on current user role
  const getDefaultRole = () => {
    return 'client_admin';
  };

  const [formData, setFormData] = useState<InvitationData>({
    email: '', // Always empty - this is what we're collecting
    firstName: existingUserData?.name?.split(' ')[0] || '',
    secondName: existingUserData?.name?.split(' ').slice(1).join(' ') || '',
    role: getDefaultRole(), // Default role based on current user
    projectId: '',
    sendEmail: true, // Always true - email is always required
    companyId: getInitialCompanyId()
  });

  // Check if user already has name
  const hasExistingName = !!existingUserData?.name;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const isSubmittingRef = useRef(false); // Ref to prevent duplicate submissions

  // Filter role options based on current user role
  const allRoleOptions = getLocalizedUserRoleOptions(t);
  const roleOptions = currentUserRole === 'internal_admin' 
    ? allRoleOptions.filter(role => role.value === 'client_admin' || role.value === 'internal_admin')
    : currentUserRole === 'client_admin'
    ? allRoleOptions.filter(role => role.value === 'client_admin')
    : allRoleOptions;


  // Create project options with format: "projectName - companyName"
  const getCompanyName = (companyId?: string): string => {
    if (!companyId) return '';
    const company = companies.find(comp => comp.id === companyId);
    return company?.name || '';
  };

  const projectOptions = projects.map(project => ({
    value: project.id,
    label: `${project.projectName} - ${getCompanyName(project.companyId)}`
  }));

  // Determine if current user can select roles freely
  // Only internal_admin and client_admin can select roles
  const canSelectRole = currentUserRole === 'internal_admin' || currentUserRole === 'client_admin';

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Update form data when modal opens with new user data
  useEffect(() => {
    if (isOpen && existingUserData) {
      setFormData({
        email: '', // Always empty - this is what we're collecting
        firstName: existingUserData.name?.split(' ')[0] || '',
        secondName: existingUserData.name?.split(' ').slice(1).join(' ') || '',
        role: getDefaultRole(),
        projectId: '',
        sendEmail: true, // Always true - email is always required
        companyId: getInitialCompanyId()
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingUserData, currentUserRole, userCompany]);

  // Auto-set company when role changes to internal_admin
  useEffect(() => {
    if (formData.role === 'internal_admin') {
      setFormData(prev => ({
        ...prev,
        companyId: INTERNAL_ADMIN_COMPANY_ID
      }));
    }
  }, [formData.role]);

  // Ensure company is auto-set for client_admin when modal opens
  useEffect(() => {
    if (isOpen && currentUserRole === 'client_admin' && userCompany) {
      setFormData(prev => ({
        ...prev,
        companyId: userCompany
      }));
    }
  }, [isOpen, currentUserRole, userCompany]);

  // Auto-set role and company when bulk import tab is selected
  useEffect(() => {
    if (activeTab === 1) {
      setFormData(prev => ({
        ...prev,
        role: 'client_admin',
        companyId: prev.companyId || getInitialCompanyId() // Ensure companyId is set
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentUserRole]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent duplicate submissions using ref for immediate protection
    if (isSubmittingRef.current || loading) {
      return;
    }
    
    // Set ref immediately to prevent race conditions
    isSubmittingRef.current = true;
    
    // Validate required fields with proper type checking
    const isFirstNameValid = formData.firstName && formData.firstName.trim().length > 0;
    const isSecondNameValid = formData.secondName && formData.secondName.trim().length > 0;
    const isEmailValid = formData.email && formData.email.trim().length > 0; // Email is always required now
    const isCompanyIdValid = formData.companyId && formData.companyId.trim().length > 0;
    // Project validation removed - project dropdown is hidden for now
    
    if (!isFirstNameValid || !isSecondNameValid || !isEmailValid || !isCompanyIdValid) {
      setError(t('invitation_modal_required_fields_error'));
      isSubmittingRef.current = false;
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Check if email already exists (email is always required now)
      // Do this check right before creating to prevent race conditions
      if (formData.email?.trim()) {
        const emailToCheck = formData.email.trim().toLowerCase();
        
        // Check Firestore for existing user with this email
        const usersQuery = query(
          collection(db, 'users'),
          where('email', '==', emailToCheck)
        );
        const existingUsers = await getDocs(usersQuery);
        
        if (!existingUsers.empty) {
          setError(t('invitation_modal_email_already_exists') || 'A user with this email already exists. Please use a different email.');
          setLoading(false);
          isSubmittingRef.current = false;
          return;
        }
        // Note: The sendInvitation Cloud Function also checks Firebase Auth for existing accounts
      }
      
      // Create user document for the database
      const userData: any = {
        firstName: formData.firstName.trim(),
        lastName: formData.secondName.trim(),
        role: formData.role || 'client_admin',
        company: formData.companyId,
        status: 'invited' as const,
        createdAt: new Date().getTime(),
        invitedBy: userId,
        invitedAt: new Date().getTime()
      };


      // Email is always required now
      if (formData.email?.trim()) {
        userData.email = formData.email.trim().toLowerCase(); // Normalize email to lowercase
      }
      
      // Save projectId
      if (formData.projectId) {
        userData.projectId = formData.projectId;
      }

      // Add user to the "users" collection
      const newUserRef = await addDoc(collection(db, 'users'), userData);

      // Update the executing user's familyMembers field
      const executingUserRef = doc(db, 'users', userId);
      await updateDoc(executingUserRef, {
        familyMembers: arrayUnion(newUserRef.id)
      });

      // Send invitation email (always required now)
      if (formData.email?.trim()) {
        try {
          const response = await fetch('https://sendinvitation-kahfqpgsrq-ey.a.run.app', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: newUserRef.id, // The user document ID that was just created
              email: formData.email.trim(),
              inviterId: userId,
              inviterName: firstName && lastName 
                ? `${firstName} ${lastName}` 
                : 'Unknown',
              inviterEmail: currentUserEmail || '',
              companyId: formData.companyId,
              role: formData.role || 'client_admin',
              language: 'en' // You might want to get this from user context
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.warn('Failed to send invitation email:', errorData.error);
            // Still show success for user creation, but mention email issue
            setSuccess(t('invitation_modal_success_message') + ' ' + t('invitation_modal_email_warning'));
          } else {
            await response.json();
          }
        } catch (emailError) {
          console.warn('Failed to send invitation email:', emailError);
          // Still show success for user creation, but mention email issue
          setSuccess(t('invitation_modal_success_message') + ' ' + t('invitation_modal_email_warning'));
        }
      }

      // Global success toast and immediate close + parent refresh
      showToast(t('invitation_modal_success_message'), 'success');

      // Close modal immediately
      onClose();

      // Reset form
      setFormData({
        email: '',
        firstName: '',
        secondName: '',
        role: getDefaultRole(),
        projectId: '',
        sendEmail: false,
        companyId: getInitialCompanyId()
      });

      // Reset states
      setLoading(false);
      setSuccess(null);
      isSubmittingRef.current = false;

      // Notify parent to refresh/switch tab
      if (onUserAdded) {
        onUserAdded();
      }

    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to add user:', err);
      }
      setError(t('invitation_modal_error_message'));
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleBulkImport = async () => {
    if (!selectedFile) {
      setError(t('invitation_modal_bulk_import_error'));
      return;
    }

    if (!formData.companyId) {
      setError('Please select a company for bulk import');
      return;
    }

    setBulkLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Upload file to Firebase Storage first
      const { getStorage, ref, uploadBytes } = await import('firebase/storage');
      const storage = getStorage();
      const fileRef = ref(storage, `bulk-imports/${Date.now()}-${selectedFile.name}`);
      await uploadBytes(fileRef, selectedFile);

      // Call the cloud function in dry-run mode to get preview data
      const response = await fetch('https://europe-west1-thescon.cloudfunctions.net/importUsersFromSheet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bucket: 'thescon.firebasestorage.app', // Your Firebase Storage bucket
          filePath: fileRef.fullPath,
          collection: 'users',
          companyId: formData.companyId,
          dryRun: true // Get preview data without importing
        }),
      });

      const result = await response.json();

      if (result.success && result.data.sample) {
        // Transform the sample data into our preview format
        const transformedData = result.data.sample.map((row: any, index: number) => {
          // Build a trimmed, lowercase lookup for column headers to handle accidental spaces/casing
          const lc: Record<string, any> = {};
          Object.entries(row || {}).forEach(([key, value]) => {
            if (typeof key === 'string') {
              lc[key.toLowerCase().trim()] = value;
            }
          });
          const normalizeKey = (k: string) => k
            .normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '') // strip diacritics
            .replace(/[^a-z0-9]+/gi, '')
            .toLowerCase();
          const nk: Record<string, any> = {};
          Object.entries(row || {}).forEach(([key, value]) => {
            if (typeof key === 'string') {
              nk[normalizeKey(key)] = value;
            }
          });
          const getAny = (...keys: string[]) => {
            for (const k of keys) {
              if (!k) continue;
              const exact = (row as any)[k];
              if (exact !== undefined && exact !== null && `${exact}`.trim() !== '') return exact;
              const lower = lc[k.toLowerCase().trim()];
              if (lower !== undefined && lower !== null && `${lower}`.trim() !== '') return lower;
              const norm = nk[normalizeKey(k)];
              if (norm !== undefined && norm !== null && `${norm}`.trim() !== '') return norm;
            }
            return '';
          };
          const getContains = (needle: string) => {
            const n = needle.toLowerCase();
            for (const [k, v] of Object.entries(lc)) {
              if (k.includes(n) && v !== undefined && v !== null && `${v}`.trim() !== '') {
                return v;
              }
            }
            return '';
          };
          if (index === 0) {
            try {
              // Debug: surface incoming keys once in console to diagnose headers
              // eslint-disable-next-line no-console

              // eslint-disable-next-line no-console

            } catch {}
          }

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const normalizeDate = (raw: any): string => {
            if (!raw) return '';
            const s = String(raw).trim();
            if (!s || s.toLowerCase() === 'dd.mm.yyyy') return '';
            // Already ISO yyyy-mm-dd
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
            // German dd.mm.yyyy
            const mDot = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
            if (mDot) {
              const d = mDot[1].padStart(2, '0');
              const mo = mDot[2].padStart(2, '0');
              const y = mDot[3];
              return `${y}-${mo}-${d}`;
            }
            // Slash formats M/D/YYYY or D/M/YYYY
            const mSlash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
            if (mSlash) {
              const a = parseInt(mSlash[1], 10);
              const b = parseInt(mSlash[2], 10);
              const y = mSlash[3];
              // If second part > 12, it's definitely D/M/Y
              let month = a;
              let day = b;
              if (b > 12 && a <= 12) {
                month = b;
                day = a;
              }
              const mm = String(month).padStart(2, '0');
              const dd = String(day).padStart(2, '0');
              return `${y}-${mm}-${dd}`;
            }
            // Fallback to Date.parse
            const t = Date.parse(s);
            if (!isNaN(t)) {
              const dt = new Date(t);
              const yyyy = dt.getFullYear();
              const mm = String(dt.getMonth() + 1).padStart(2, '0');
              const dd = String(dt.getDate()).padStart(2, '0');
              return `${yyyy}-${mm}-${dd}`;
            }
            return '';
          };

          return {
            id: `preview-${index}`,
            salutation: row.salutation || row.anrede || row['Anrede'] || '',
            title: getAny('title', 'titel', 'Titel', 'Title') || getContains('titel') || '',
            firstName: row.firstName || row.vorname || row.first_name || row['Vorname'] || '',
            secondName: row.lastName || row.nachname || row.last_name || row['Nachname'] || '',
            email: row.email || row.e_mail || row['E-Mail'] || row['Email'] || '',
            project: row.project || row.tariff || formData.projectId || '', // Use selected projectId as fallback
            role: formData.role || 'client_admin', // Use selected role
            status: 'valid' as const,
            errors: [],
            warnings: []
          };
        });

        setPreviewData(transformedData);
        setShowPreview(true);
      } else {
        setError(result.error || 'Failed to process file');
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Bulk import failed:', err);
      }
      setError(t('invitation_modal_bulk_import_processing_error'));
    } finally {
      setBulkLoading(false);
    }
  };

  const handlePreviewComplete = (importedCount: number) => {
    setShowPreview(false);
    const msg = t('invitation_modal_bulk_import_success').replace('{count}', importedCount.toString());
    showToast(msg, 'success');
    
    // Reset form
    setSelectedFile(null);
    setPreviewData([]);
    
    // Callback to refresh user list
    if (onUserAdded) {
      onUserAdded();
    }
    
    // Close modal immediately
    onClose();
  };

  const handlePreviewCancel = () => {
    setShowPreview(false);
    setPreviewData([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv' // .csv
      ];
      
      if (validTypes.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setError(null);
      } else {
        setError(t('invitation_modal_file_validation_error'));
        setSelectedFile(null);
      }
    }
  };

  const handleInputChange = (field: keyof InvitationData, value: string | boolean | number | null) => {
    setFormData((prev: InvitationData) => ({
      ...prev,
      [field]: value
    }));
  };


  const clearError = () => setError(null);
  const clearSuccess = () => setSuccess(null);

  if (!isOpen) return null;

  // Show preview component if in preview mode
  if (showPreview) {
    return (
      <EmployeeImportPreview
        employees={previewData}
        onImportComplete={handlePreviewComplete}
        onCancel={handlePreviewCancel}
        inviterId={userId}
        inviterName={firstName && lastName ? `${firstName} ${lastName}` : 'Unknown'}
        inviterEmail={currentUserEmail || ''}
        companyId={formData.companyId}
        selectedRole={formData.role}
        selectedProject={formData.projectId}
      />
    );
  }

  const tabs = [
    {
      label: t('invitation_modal_individual_tab'),
      content: (
        <form id={`invitation-form-${userId}`} onSubmit={handleSubmit} className={styles.form}>
          {/* Role Selection - First and most important */}
          <GlobalDropdown
            label={t('invitation_modal_user_role_label')}
            value={formData.role || 'client_admin'}
            onChange={(value) => handleInputChange('role', value)}
            options={roleOptions}
            placeholder={t('invitation_modal_user_role_placeholder')}
            required
            disabled={!canSelectRole}
          />

          {/* Company Selection - Only shown for internal_admin */}
          {currentUserRole === 'internal_admin' && (
            <GlobalDropdown
              label={t('invitation_modal_company_label')}
              value={formData.companyId || ''}
              onChange={(value) => handleInputChange('companyId', value)}
              options={filteredCompanies.map(company => ({
                value: company.id,
                label: company.name
              }))}
              placeholder={companiesLoading ? t('invitation_modal_company_loading') : t('invitation_modal_company_placeholder')}
              required
              disabled={formData.role === 'internal_admin' || !!preselectedCompanyId}
            />
          )}

          {/* Project selection - Hidden for now, users aren't mapped to projects yet */}
          {/* {(formData.role === 'client_employee' || formData.role === 'family_member') && 
           (currentUserRole === 'internal_admin' || currentUserRole === 'client_admin') && (
            <>
              <GlobalDropdown
                label={t('invitation_modal_project_label')}
                value={formData.projectId || ''}
                onChange={(value) => handleInputChange('projectId', value)}
                options={projectOptions}
                placeholder={projectsLoading || companiesLoading ? (t('invitation_modal_project_loading') || 'Loading projects...') : t('invitation_modal_project_placeholder')}
                required
                disabled={projectsLoading || companiesLoading}
              />
            </>
          )} */}

          {/* First Name */}
          <GlobalInput
            label={t('invitation_modal_first_name_label')}
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            required
            disabled={hasExistingName}
          />

          {/* Second Name */}
          <GlobalInput
            label={t('invitation_modal_second_name_label')}
            type="text"
            value={formData.secondName}
            onChange={(e) => handleInputChange('secondName', e.target.value)}
            required
            disabled={hasExistingName}
          />

          {/* Email - always shown, email is always required */}
          <GlobalInput
            label={t('invitation_modal_email_label')}
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />
        </form>
      )
    },
    {
      label: t('invitation_modal_bulk_import_tab'),
      content: (
        <div className={styles.bulkImportContainer}>
          {/* Role Selection - Auto-set based on user role and disabled */}
          <GlobalDropdown
            label={t('invitation_modal_user_role_label')}
            value="client_admin"
            onChange={() => {}} // Disabled, no action needed
            options={roleOptions}
            placeholder={t('invitation_modal_user_role_placeholder')}
            required
            disabled={true}
          />

          {/* Company Selection - Only shown for internal_admin */}
          {currentUserRole === 'internal_admin' && (
            <GlobalDropdown
              label={t('invitation_modal_company_label')}
              value={formData.companyId || ''}
              onChange={(value) => handleInputChange('companyId', value)}
              options={filteredCompanies.map(company => ({
                value: company.id,
                label: company.name
              }))}
              placeholder={companiesLoading ? t('invitation_modal_company_loading') : t('invitation_modal_company_placeholder')}
              required
              disabled={!!preselectedCompanyId}
            />
          )}

          {/* Project Selection - Only for internal_admin and client_admin */}
          {(currentUserRole === 'internal_admin' || currentUserRole === 'client_admin') && (
            <GlobalDropdown
              label={t('invitation_modal_project_label')}
              value={formData.projectId || ''}
              onChange={(value) => handleInputChange('projectId', value)}
              options={projectOptions}
                placeholder={projectsLoading || companiesLoading ? (t('invitation_modal_project_loading') || 'Loading projects...') : t('invitation_modal_project_placeholder')}
                required
                disabled={projectsLoading || companiesLoading}
            />
          )}

          <div className={styles.fileUploadContainer}>
            <input
              type="file"
              id="bulkFile"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className={styles.fileInput}
            />
            <label htmlFor="bulkFile" className={styles.fileLabel}>
              {selectedFile ? selectedFile.name : t('invitation_modal_file_select_placeholder')}
            </label>
          </div>
          <p className={styles.fileHelpText}>
            {t('invitation_modal_file_help_text')}
          </p>
        </div>
      )
    }
  ];

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>

        {/* Header */}
        <div className={styles.header}>
          <h2>{t('invitation_modal_title')}</h2>
          <button onClick={onClose} className={styles.closeButton}>
            {t('invitation_modal_close_button')}
          </button>
        </div>

        {/* Form - Tabs hidden for now, only showing individual invitation */}
        {/* <GlobalTabs 
          tabs={tabs} 
          initialIndex={activeTab}
          onChange={setActiveTab}
        /> */}
        <div className={styles.formContainer}>
          {tabs[0].content}
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <GlobalButton
            type="button"
            onClick={onClose}
            disabled={loading || bulkLoading}
            variant="secondary"
          >
            {t('invitation_modal_cancel_button')}
          </GlobalButton>
          <GlobalButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleSubmit(e as any);
            }}
            isLoading={loading}
            disabled={loading || !formData.firstName.trim() || !formData.secondName.trim() || !formData.email.trim() || !formData.companyId}
          >
            {t('invitation_modal_confirm_button')}
          </GlobalButton>
        </div>

        {/* Error toast */}
        {error && (
          <Toast
            message={error}
            type="error"
            isVisible={true}
            onClose={clearError}
          />
        )}

        {/* Success toast */}
        {success && (
          <Toast
            message={success}
            type="success"
            isVisible={true}
            onClose={clearSuccess}
          />
        )}
      </div>
    </div>
  );
}
